import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';

type Drift = 'none' | 'left' | 'right' | 'vertical';
type FrenchResult = 'untested' | 'on-line' | 'left' | 'right';

interface WalkBackData {
  /** Has user shot all 4 distances (20/40/50/60)? */
  shotAllDistances?: boolean;
  drift?: Drift;

  // French tune variant (v119 — Dudley's plumb-line two-distance method).
  frenchPlumbHung?: boolean;
  frenchNear?: FrenchResult;
  frenchFar?: FrenchResult;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 9 — Walk-Back Tune
 *
 * Use the 20-yard pin to shoot at 20, 40, 50, 60 yards. The arrow drops at each
 * distance — if the rest is in spec, all impacts fall on a vertical line. Drift
 * left/right at distance = micro-adjust rest. Vertical inconsistency = spine
 * or broadhead alignment.
 */
export function WalkBackStep({ tuning }: Props) {
  const data = (tuning.session.stepData['walk_back'] as WalkBackData | undefined) ?? {};
  const update = (patch: Partial<WalkBackData>) =>
    tuning.updateStepData<WalkBackData>('walk_back', { ...data, ...patch });

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Using ONLY your 20-yard pin, shoot at 20, 40, 50, 60 yards. The arrows drop at each
        distance (normal — that's why you have other pins) but they should all hit on a
        single vertical line. Left/right drift at distance = rest needs micro-adjustment.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Procedure</h3>
          <CheatSheetButton defaultSheet="adjustment-matrix" label="Adjustment chart" />
        </div>
        <ol className="list-decimal list-inside space-y-1 text-[13px] text-zinc-300 leading-relaxed">
          <li>Set up a tall vertical target (large bale, big foam block) at 20 yards.</li>
          <li>Shoot 3 arrows at 20 yards using your 20-yard pin — note the group center.</li>
          <li>Walk back to 40, 50, 60 yards. Shoot 3 arrows at each distance using the SAME 20-yard pin.</li>
          <li>Connect the group centers. They should form a straight vertical line.</li>
        </ol>

        <label className="flex items-start gap-2 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={!!data.shotAllDistances}
            onChange={(e) => update({ shotAllDistances: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <span className="text-sm">I've shot all four distances (20, 40, 50, 60)</span>
        </label>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">What does your pattern look like?</h3>
        <div className="space-y-2">
          <Choice label="✓ All on a vertical line — no drift" selected={data.drift === 'none'} onClick={() => update({ drift: 'none' })} />
          <Choice label="← Drifts LEFT as distance increases (RH)" selected={data.drift === 'left'} onClick={() => update({ drift: 'left' })} />
          <Choice label="→ Drifts RIGHT as distance increases (RH)" selected={data.drift === 'right'} onClick={() => update({ drift: 'right' })} />
          <Choice label="↕ Vertical inconsistency (not on a single line)" selected={data.drift === 'vertical'} onClick={() => update({ drift: 'vertical' })} />
        </div>
      </Card>

      {data.drift === 'none' && (
        <Status ok>
          Walk-back is clean — your rest is in the right L/R position and your form is consistent.
          Ready for broadhead group testing.
        </Status>
      )}

      {data.drift === 'left' && (
        <TipCard title="Drift LEFT at distance — micro-adjust rest" defaultOpen>
          <p className="mb-2">
            The arrow leaves the bow with a slight rightward yaw that compounds with flight time.
            Move rest LEFT in tiny increments.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Loosen rest windage lock.</li>
            <li>Move rest LEFT (toward riser) 1/64" or 1/100 turn of micro-adjust screw.</li>
            <li>Re-shoot 20 + 60 yard groups. Adjust again if drift persists.</li>
            <li>Each 1/100 turn typically corrects ~1" drift at 60 yards.</li>
          </ol>
        </TipCard>
      )}

      {data.drift === 'right' && (
        <TipCard title="Drift RIGHT at distance — micro-adjust rest" defaultOpen>
          <p className="mb-2">
            The arrow leaves the bow with a slight leftward yaw. Move rest RIGHT in tiny increments.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Loosen rest windage lock.</li>
            <li>Move rest RIGHT (away from riser) 1/64" or 1/100 turn of micro-adjust screw.</li>
            <li>Re-shoot 20 + 60 yard groups. Adjust again if drift persists.</li>
            <li>Each 1/100 turn typically corrects ~1" drift at 60 yards.</li>
          </ol>
        </TipCard>
      )}

      {data.drift === 'vertical' && (
        <TipCard title="Vertical inconsistency — diagnose" defaultOpen>
          <p className="mb-2">Arrows hitting on different vertical lines at each distance usually means:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>
              <strong>Spine mismatch</strong> — your arrow's dynamic spine is off for the bow weight + arrow
              length + point weight combo. Check the arrow builder's spine recommendation.
            </li>
            <li>
              <strong>Inconsistent form</strong> — different anchor point, grip torque, or release. Best test:
              have a 2nd shooter try the bow.
            </li>
            <li>
              <strong>Broadhead alignment</strong> (if shooting BH) — bent or misaligned BH ferrule. Use a spin
              tester to check.
            </li>
            <li>
              <strong>Wind</strong> — even mild crosswind throws vertical at 60 yards. Re-test on a calm day.
            </li>
          </ol>
        </TipCard>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          FRENCH TUNE (v119 — Dudley's plumb-line two-distance method).
          Finer L/R signal than walk-back alone. Vertical drop is irrelevant
          because the plumb line is the reference — only L/R matters.
          ────────────────────────────────────────────────────────────── */}
      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">French tune (optional · finer L/R signal)</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            Plumb-line method
          </span>
        </div>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Walk-back tells you whether the rest is centered. French tune tells you
          whether the rest is centered AND whether the rest is at the right
          height in one shot. It's a parallel method, not a replacement — run it
          after walk-back for fine adjustments. The plumb line removes vertical
          drop from the signal entirely, so any deviation is pure L/R.
        </p>

        <InfoCard title="How to French-tune (no shop tools)">
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>
              Hang a real plumb line at the target — a string with a weight, or
              a long carpenter's plumb-bob. Tape it to the top of the target so
              it hangs straight down to the bottom.
            </li>
            <li>
              Step to a CLOSE distance (5–10 yards). Shoot 3 arrows aiming at the
              top of the plumb line. They should land directly on the line.
            </li>
            <li>
              Step to a FAR distance (40–60 yards). Shoot 3 arrows aiming at the
              same top point. They will drop, but they should still land
              <em> on the same vertical line</em>. Any L/R deviation is the rest.
            </li>
            <li>
              Adjust rest L/R until BOTH close and far impacts land on the line.
            </li>
          </ol>
          <p className="text-[11px] text-zinc-400 mt-2">
            Why this is finer than walk-back: vertical drop is irrelevant — the
            plumb line is reference, gravity does what gravity does. Every bit of
            signal you see is pure horizontal, which is exactly what the rest
            controls. Less noise = faster convergence.
          </p>
        </InfoCard>

        <label className="flex items-start gap-2 cursor-pointer mt-4">
          <input
            type="checkbox"
            className="mt-1"
            checked={data.frenchPlumbHung ?? false}
            onChange={(e) => update({ frenchPlumbHung: e.target.checked })}
          />
          <span className="text-sm text-zinc-300">Plumb line hung on the target?</span>
        </label>

        {data.frenchPlumbHung && (
          <div className="mt-3 space-y-3">
            <Field label="Close-distance group (5–10 yd) — on the line?">
              <select
                value={data.frenchNear ?? 'untested'}
                onChange={(e) => update({ frenchNear: e.target.value as FrenchResult })}
                className={inputClass}
              >
                <option value="untested">Not tested yet</option>
                <option value="on-line">On the line · close range is clean</option>
                <option value="left">Drifts LEFT</option>
                <option value="right">Drifts RIGHT</option>
              </select>
            </Field>

            {data.frenchNear && data.frenchNear !== 'untested' && (
              <Field label="Far-distance group (40–60 yd) — on the line?">
                <select
                  value={data.frenchFar ?? 'untested'}
                  onChange={(e) => update({ frenchFar: e.target.value as FrenchResult })}
                  className={inputClass}
                >
                  <option value="untested">Not tested yet</option>
                  <option value="on-line">On the line · rest is dialed</option>
                  <option value="left">Drifts LEFT at distance</option>
                  <option value="right">Drifts RIGHT at distance</option>
                </select>
              </Field>
            )}

            {data.frenchNear === 'on-line' && data.frenchFar === 'on-line' && (
              <div className="text-sm leading-relaxed p-2.5 rounded border border-emerald-700/40 bg-emerald-950/20 text-emerald-300">
                Both ranges land on the plumb line. Rest is dialed L/R in both
                regimes. Move to the broadhead group test.
              </div>
            )}

            {(data.frenchFar === 'left' || data.frenchFar === 'right') &&
              data.frenchNear === 'on-line' && (
                <div className="text-sm leading-relaxed p-2.5 rounded border border-amber-700/40 bg-amber-950/20 text-amber-300">
                  Close range is centered but distance is drifting{' '}
                  {data.frenchFar === 'left' ? 'LEFT' : 'RIGHT'}. This is a pure
                  rest position issue (close-range hit means launch direction
                  is correct; distance drift means rest is slightly off
                  centerline). Move the rest {data.frenchFar === 'left' ? '~0.003" RIGHT' : '~0.003" LEFT'}{' '}
                  (away from the drift) and re-shoot both distances.
                </div>
              )}

            {(data.frenchNear === 'left' || data.frenchNear === 'right') && (
              <div className="text-sm leading-relaxed p-2.5 rounded border border-amber-700/40 bg-amber-950/20 text-amber-300">
                Close range is drifting{' '}
                {data.frenchNear === 'left' ? 'LEFT' : 'RIGHT'}. Center-shot is
                off — back up two steps to Center Shot and re-set with the bow
                tip aligned to your spec offset (typically 13/16" for hunting
                rigs). French tune is a fine-adjustment tool, not a fix for a
                mis-set center shot.
              </div>
            )}
          </div>
        )}
      </Card>

      <InfoCard title="Why use the 20-yard pin for all distances?">
        <p>
          Walk-back tests REST position, not sight calibration. Using the 20-yard pin at
          all distances means the pin doesn't move — only the arrow drops naturally as
          flight time increases. The rest is the only variable that could cause horizontal
          drift across the line. That makes the test diagnose rest position with no other
          noise.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Walk-back tests require a generous backstop — arrows drop a lot at 60 yards with
        a 20-yard pin (often 8+ feet below your aim point). Make sure your target frame is
        TALL enough to catch the worst-case drop, and that the area behind it is clear.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}
